import '../../assets/styles/login.scss';
import '../../assets/styles/playground.scss';
import PlaygroundTopHeader from '../../components/playground-v2/PlaygroundTopHeader';
import PlaygroundEventBannerPopup from '../../components/playground-v2/PlaygroundEventBannerPopup';
import PlaygroundV2RenewalPopup, { canShowRenewalPopup } from '../../components/playground-v2/PlaygroundV2RenewalPopup';
import PlaygroundPointCard from '../../components/playground-v2/PlaygroundPointCard';
import PlaygroundHeroCarousel from '../../components/playground-v2/PlaygroundHeroCarousel';
import PlaygroundPedometerCard from '../../components/playground-v2/PlaygroundPedometerCard';
import PlaygroundRouteCard from '../../components/playground-v2/PlaygroundRouteCard';
import PlaygroundMissionCard from '../../components/playground-v2/PlaygroundMissionCard';
import PlaygroundMiniGamesCardRow from '../../components/playground-v2/PlaygroundMiniGamesCardRow';
import PlaygroundStackingBannerList from '../../components/playground-v2/PlaygroundStackingBannerList';
import PlaygroundQuickRewardRow from '../../components/playground-v2/PlaygroundQuickRewardRow';
import PlaygroundSnsCardRow from '../../components/playground-v2/PlaygroundSnsCardRow';
import BuzzvilAd from '../../components/BuzzvilAd';
import PointImg from '../../assets/images/img_point.png';
import { ReactComponent as VectorWave } from '../../assets/icons/vector-wave.svg';
import { ReactComponent as VectorWaveBlack } from '../../assets/icons/vector-wave-black.svg';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useRunTimeEnvironment } from '../../utils/hooks';
import useSWR from 'swr';
import ModalDialog from '../../components/common/Modal';
import { TextField } from '../../components/common/Input';
import Button from '../../components/common/Button';
import ToastAlert from '../../components/common/Toast';
import AttendanceBottomSheet from '../../components/attendance/AttendanceBottomSheet';
import { useAttendanceClaim } from '../../components/attendance/useAttendanceClaim';
import { useAttendanceInfo } from '../../hooks/useAttendanceInfo';
import { usePedometerBridge } from '../../hooks/usePedometerBridge';
import { usePedometerClaim } from '../../hooks/usePedometerClaim';
import { markSwipeHintDone } from '../../components/playground-v2/PedometerSwipeHint';
import PedometerPermissionSheet, {
  isPedometerConsentSeen,
  markPedometerConsentSeen,
} from '../../components/playground-v2/PedometerPermissionSheet';
import PedometerPermissionReaskModal from '../../components/playground-v2/PedometerPermissionReaskModal';
import { useToast } from '../../context/ToastContext';

const PLAYGROUND_SCROLL_KEY = 'playground-scroll-top';
const ALERT_TIMEOUT = 2000;
const PEDOMETER_POLL_DEBUG_TOAST = false;

function PlaygroundPage() {
  const RTE = useRunTimeEnvironment();
  const { showToast } = useToast();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const [adParams, setAdParams] = useState({});
  const [friendCode, setFriendCode] = useState('');
  const [friendCodeOpen, setFriendCodeOpen] = useState(true);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState(false);
  const [attendanceSheetDismissed, setAttendanceSheetDismissed] = useState(false);
  const [attendanceSheetOpen, setAttendanceSheetOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(() => {
    try {
      const raw = Number(localStorage.getItem('playground-hero-panel'));
      return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 0;
    } catch (error) {
      return 0;
    }
  });
  const [pedometerSceneVisible, setPedometerSceneVisible] = useState(true);
  const [pedometerSheetOpen, setPedometerSheetOpen] = useState(false);
  const [pedometerReaskOpen, setPedometerReaskOpen] = useState(false);
  const { data: authProfile, isLoading: isAuthProfileLoading } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/auth/user/check`, accessToken],
    fetchWithToken,
  );
  const { data: profileInfo } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/user/profile/info`, accessToken],
    fetchWithToken,
  );
  const { data: attendanceInfo, mutate: mutateAttendance } = useAttendanceInfo();
  const onPollNativeSteps = useCallback(
    (payload) => {
      const health = Math.max(0, Number(payload?.healthStep ?? payload?.step) || 0);
      const sync = payload?.syncStatus ?? '-';
      showToast(`폴링 · HC ${health.toLocaleString('ko-KR')} · ${sync}`, { timeout: 2000 });
    },
    [showToast],
  );
  const {
    today: pedometerToday,
    mutate: mutatePedometer,
    displaySteps,
    healthSteps,
    displayKm,
    permission: stepPermission,
    needsHealthAppGuide,
    nativeSupported: pedometerNativeSupported,
    requestPermission: requestStepPermission,
    openHealthApp: openHealthAppFromBridge,
  } = usePedometerBridge({
    active: heroIndex === 0 && pedometerSceneVisible,
    onPollNativeSteps:
      PEDOMETER_POLL_DEBUG_TOAST && RTE === 'APP' ? onPollNativeSteps : undefined,
  });
  const pedometerLive = RTE !== 'APP' || pedometerNativeSupported;
  const showAttendanceToast = useCallback(
    (message) => {
      showToast(message, { timeout: ALERT_TIMEOUT });
    },
    [showToast],
  );
  const { onCta: onAttendanceCta, busy: attendanceBusy } = useAttendanceClaim({
    data: attendanceInfo,
    mutate: mutateAttendance,
    onToast: showAttendanceToast,
  });
  const { onSegmentTap: onPedometerSegmentTap } = usePedometerClaim({
    today: pedometerToday,
    mutate: mutatePedometer,
    onToast: showAttendanceToast,
  });

  useEffect(() => {
    if (heroIndex === 1) {
      markSwipeHintDone();
    }
  }, [heroIndex]);

  const openOfferwallMain = () => {
    if (RTE === 'APP') {
      window.flutter_inappwebview?.callHandler?.('goto_offerwall_main');
    }
  };

  const getReward = async () => {
    if (!authProfile?.userInfo) return;

    if (authProfile.refCode === friendCode) {
      alert('스스로 초대 할 수 없어요.');
      return;
    }

    const response = await fetch(`${API_SERVER_DOMAIN}/user/ref/submit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ref_code: friendCode,
      }),
    });

    const jsonData = await response.json();
    if (jsonData.result === 'ok') {
      setAlertMessage(`소환 코드를 통해 가입하신 ${authProfile.userInfo.nickName}님께 200포인트가 지급 되었어요.`);
    } else {
      setAlertMessage(jsonData.message);
    }
    setFriendCodeOpen(false);

    setTimeout(() => {
      setWelcomeOpen(true);
    }, ALERT_TIMEOUT - 300);
  };

  useEffect(() => {
    if (RTE !== 'APP') return;
    const userNo = authProfile?.userInfo?.userNo;
    if (!accessToken || !userNo) return;
    window.flutter_inappwebview?.callHandler?.('grantAuthInfo', accessToken, userNo);
  }, [RTE, accessToken, authProfile?.userInfo?.userNo]);

  useEffect(() => {
    const getNativeInfo = async () => {
      const info = await window.flutter_inappwebview?.callHandler?.('get_native_play');
      if (!info) return;
      setAdParams({
        platform: appInfo?.os?.substring(0, 1).toUpperCase(),
        unitId: info.unit_id,
        ifa: info.ifa,
        ifv: info.ifv,
      });
    };

    if (RTE === 'APP') {
      getNativeInfo();
    }

    const pageContainer = document.getElementsByClassName('page-container')[0];
    if (!pageContainer) return;

    const saveScroll = () => {
      const top = Math.max(pageContainer.scrollTop ?? 0, window.scrollY || 0);
      sessionStorage.setItem(PLAYGROUND_SCROLL_KEY, String(top));
    };

    pageContainer.addEventListener('scroll', saveScroll, { passive: true });

    return () => {
      pageContainer.removeEventListener('scroll', saveScroll);
    };
  }, [RTE, appInfo?.os]);

  useEffect(() => {
    const pageContainer = document.getElementsByClassName('page-container')[0];
    if (!pageContainer) return;

    const raw = sessionStorage.getItem(PLAYGROUND_SCROLL_KEY);
    if (!raw) return;

    const targetTop = Number(raw);
    if (!Number.isFinite(targetTop) || targetTop <= 0) return;

    let tries = 0;
    const restore = () => {
      pageContainer.scrollTo(0, targetTop);
      window.scrollTo(0, targetTop);
      const restored = Math.abs(pageContainer.scrollTop - targetTop) < 2;
      if (!restored && tries < 20) {
        tries += 1;
        requestAnimationFrame(restore);
      }
    };

    requestAnimationFrame(restore);
  }, []);

  const ackAttendancePopup = useCallback(async () => {
    if (!accessToken) return;
    try {
      await fetch(`${API_SERVER_DOMAIN}/main/attendance/popup-shown`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      mutateAttendance((current) => (current ? { ...current, showPopup: false } : current), {
        revalidate: false,
      });
    } catch (error) {
      console.error('attendance popup-shown error', error);
    }
  }, [API_SERVER_DOMAIN, accessToken, mutateAttendance]);

  const blockingFirstLogin = Boolean(authProfile?.isFirstLogin && friendCodeOpen);

  useEffect(() => {
    if (
      attendanceInfo?.showPopup &&
      !attendanceSheetDismissed &&
      Boolean(authProfile?.userInfo) &&
      !isAuthProfileLoading &&
      !blockingFirstLogin &&
      !welcomeOpen
    ) {
      setAttendanceSheetOpen(true);
    }
  }, [
    attendanceInfo?.showPopup,
    attendanceSheetDismissed,
    authProfile?.userInfo,
    blockingFirstLogin,
    isAuthProfileLoading,
    welcomeOpen,
  ]);

  useEffect(() => {
    if (!attendanceInfo?.alreadyAttendToday || !attendanceSheetOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      setAttendanceSheetOpen(false);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [attendanceInfo?.alreadyAttendToday, attendanceSheetOpen]);

  const showAttendancePopup =
    Boolean(authProfile?.userInfo) &&
    !isAuthProfileLoading &&
    !blockingFirstLogin &&
    !welcomeOpen &&
    attendanceSheetOpen;

  const showRenewalPopup =
    Boolean(authProfile?.userInfo) &&
    !isAuthProfileLoading &&
    !blockingFirstLogin &&
    !welcomeOpen &&
    !showAttendancePopup &&
    canShowRenewalPopup();

  const showEventBanner =
    Boolean(authProfile?.userInfo) &&
    !isAuthProfileLoading &&
    !friendCodeOpen &&
    !welcomeOpen &&
    !showAttendancePopup &&
    !showRenewalPopup;

  const canOfferPedometerConsent =
    RTE === 'APP' &&
    pedometerLive &&
    Boolean(authProfile?.userInfo) &&
    !isAuthProfileLoading &&
    !blockingFirstLogin &&
    !welcomeOpen &&
    !showAttendancePopup &&
    !showRenewalPopup &&
    stepPermission !== 'granted' &&
    !isPedometerConsentSeen();

  // 설치 후 최초: 하단 안내 시트 → (허용 시) HC/HK OS 권한
  useEffect(() => {
    if (!canOfferPedometerConsent || pedometerReaskOpen) {
      if (!canOfferPedometerConsent) {
        setPedometerSheetOpen(false);
      }
      return;
    }
    setPedometerSheetOpen(true);
  }, [canOfferPedometerConsent, pedometerReaskOpen]);

  useEffect(() => {
    if (stepPermission !== 'granted') {
      return;
    }
    markPedometerConsentSeen();
    setPedometerSheetOpen(false);
    setPedometerReaskOpen(false);
  }, [stepPermission]);

  const onPedometerSheetAllow = useCallback(async () => {
    setPedometerSheetOpen(false);
    markPedometerConsentSeen();
    const payload = await requestStepPermission();
    if (payload?.permission === 'granted') {
      setPedometerReaskOpen(false);
      return;
    }
    setPedometerReaskOpen(true);
  }, [requestStepPermission]);

  const onPedometerSheetDisagree = useCallback(() => {
    setPedometerSheetOpen(false);
    setPedometerReaskOpen(true);
  }, []);

  const onPedometerAllowPermission = useCallback(async () => {
    setPedometerReaskOpen(false);
    const payload = await requestStepPermission();
    if (payload?.permission === 'granted') {
      markPedometerConsentSeen();
      return;
    }
    // 재확인 후에도 거부면 플래그 저장 후 CTA만 유지 (루프 방지)
    markPedometerConsentSeen();
  }, [requestStepPermission]);

  const onPedometerSkip = useCallback(() => {
    markPedometerConsentSeen();
    setPedometerReaskOpen(false);
  }, []);

  return (
    <main className="flex min-h-screen w-full flex-col items-start justify-start bg-black">
      <AttendanceBottomSheet
        open={showAttendancePopup}
        data={attendanceInfo}
        onCta={onAttendanceCta}
        busy={attendanceBusy}
        onShown={ackAttendancePopup}
        onClose={() => {
          setAttendanceSheetDismissed(true);
          setAttendanceSheetOpen(false);
          if (!attendanceInfo?.alreadyAttendToday) {
            showAttendanceToast('놓친 출석은 출석체크에서 확인 가능해요');
          }
        }}
      />
      <PlaygroundV2RenewalPopup enabled={showRenewalPopup} />
      <PlaygroundEventBannerPopup apiDomain={API_SERVER_DOMAIN} enabled={showEventBanner} />
      <PedometerPermissionSheet
        open={pedometerSheetOpen}
        onAllow={onPedometerSheetAllow}
        onDisagree={onPedometerSheetDisagree}
      />
      <PedometerPermissionReaskModal
        open={pedometerReaskOpen}
        onAllow={onPedometerAllowPermission}
        onSkip={onPedometerSkip}
      />
      <section className="w-full">
        {/* Figma: 만보기=풀블리드 산책 씬 위에 헤더, 포인트=보라 그라데이션 위 카드
            배경은 패널별 적용 — 스와이프 중 heroIndex 전환 전에도 포인트 패널이 초록으로 비치지 않게 */}
        <div className="relative w-full bg-[#F4F4F4]">
          <PlaygroundHeroCarousel
            aspectRatio="360 / 432"
            onIndexChange={(next) => {
              setHeroIndex(next);
              // 포인트 패널로 한 번 넘어가면 유도 화살표는 다시 안 띄운다
              if (next === 1) {
                markSwipeHintDone();
              }
            }}
          >
            <div className="h-full w-full bg-[#FFFFFF]">
              <PlaygroundPedometerCard
                active={heroIndex === 0}
                today={pedometerToday}
                steps={displaySteps}
                permission={pedometerLive ? (RTE === 'APP' ? stepPermission : 'granted') : 'granted'}
                needsHealthAppGuide={pedometerLive && needsHealthAppGuide}
                onRequestPermission={requestStepPermission}
                onOpenHealthApp={openHealthAppFromBridge}
                onSegmentTap={onPedometerSegmentTap}
                onSceneVisibleChange={setPedometerSceneVisible}
                appInfo={appInfo}
              />
            </div>
            <div className="relative h-full w-full bg-gradient-to-t from-[#EAE2FF] via-[#7281DC] to-[#EAE2FF]">
              <PlaygroundPointCard point={profileInfo?.info?.point ?? 0} />
            </div>
          </PlaygroundHeroCarousel>
          <PlaygroundTopHeader
            overlay
            showPool={false}
            onPointTab={heroIndex === 1}
            showRedDot={authProfile?.showRedDot}
            steps={RTE === 'APP' && pedometerLive ? displaySteps : undefined}
            healthSteps={RTE === 'APP' && pedometerLive ? healthSteps : undefined}
            km={RTE === 'APP' && pedometerLive ? displayKm : undefined}
            onStepsClick={() => {
              if (RTE !== 'APP' || !pedometerLive) return;
              if (stepPermission === 'granted') return;
              requestStepPermission();
            }}
          />
          {/* 시안형 물결: 씬 하단과 살짝 겹쳐 경계 띠가 보이지 않게 */}
          <div data-svg-wrapper className="pointer-events-none relative z-[6] -mt-[1.6rem] w-full">
            <VectorWave className="block h-10 w-full" preserveAspectRatio="none" />
          </div>
        </div>
        <div className="bg-gray01 pb-7 px-2">
          <PlaygroundRouteCard />
          <PlaygroundMissionCard onOpenOfferwall={openOfferwallMain} />
        </div>
      </section>

      <section className="w-full bg-black">
        <div data-svg-wrapper className="relative h-3 w-full">
          <VectorWaveBlack className="absolute left-0 -top-5 block h-10 w-full" preserveAspectRatio="none" />
        </div>
        <div className="relative z-10 px-4 mb-3 self-stretch justify-center text-gray02 text-sm font-extrabold font-['Pretendard']">
          한방을 노려라 !
        </div>

        <div className="px-2">
          <PlaygroundMiniGamesCardRow />
        </div>
      </section>

      <section className="w-full bg-black py-5">
        <div className="relative z-10 px-4 mb-3 self-stretch justify-center text-gray02 text-sm font-extrabold font-['Pretendard']">
          차곡차곡 모으기
        </div>

        <PlaygroundStackingBannerList />

        <div className="relative w-full h-8 py-5">
          <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-black/90" />
          <div className="absolute left-6 right-6 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-white/75 to-transparent" />
        </div>
        <PlaygroundQuickRewardRow showAttendanceBadge={Boolean(attendanceInfo?.showBadge)} />
        
        <PlaygroundSnsCardRow />

        <div className="px-2">
        {adParams?.unitId ? (
            <div className="mt-5 rounded-[10px] bg-white py-2">
              <BuzzvilAd adParams={adParams} extraStyle="buzzilad-container-playground" />
            </div>
          ) : (
            <div className="w-full mt-5 bg-gray02 rounded-[10px]" />
          )}
        </div>

      </section>

      <ToastAlert show={!!alertMessage} onClose={setAlertMessage} text={alertMessage} timeout={ALERT_TIMEOUT} />

      {authProfile?.userInfo && (
        <>
          <ModalDialog
            title="나를 소환한 친구 코드"
            className="friend-code-modal"
            open={authProfile.isFirstLogin && friendCodeOpen}
            onCancel={() => setFriendCodeOpen(false)}
          >
            <TextField onChange={setFriendCode} defaultValue="" />
            <p className="text-light">초대받은 후 1달 내 회원탈퇴를 하시면 초대로 받으신 포인트가 모두 사라져요.</p>
            <div className="friend-nickname">
              <span>부여받은 닉네임</span>
              <span className="text-accent">{authProfile.userInfo.nickName}</span>
            </div>
            <Button onClick={() => getReward()}>확인</Button>
          </ModalDialog>

          <ModalDialog title="가입을 축하합니다!" className="welcome-modal" open={welcomeOpen}>
            <p className="text-light">
              가입 선물로 <strong>300p</strong>
              <br />
              드려요!!
            </p>
            <img src={PointImg} width={124} role="presentation" alt="" />
            <Button onClick={() => setWelcomeOpen(false)}>확인</Button>
          </ModalDialog>
        </>
      )}

    </main>
  );
}

export default PlaygroundPage;
